import { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Send,
  FileText,
  UserPlus,
  Plus,
  ArrowRight,
  Sliders,
  Eye,
  EyeOff,
  ChevronUp,
  ChevronDown,
  RotateCcw,
  X,
} from 'lucide-react';
import {
  AreaChart,
  Area,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  ResponsiveContainer,
  PieChart,
  Pie,
  Cell,
  BarChart,
  Bar,
} from 'recharts';
import { ProgressBar } from '@/components/ui/ProgressBar';
import { StatusDot } from '@/components/ui/StatusDot';
import { Timeline } from '@/components/ui/Timeline';
import { Skeleton, SkeletonText } from '@/components/ui/Skeleton';
import { EmptyState } from '@/components/ui/EmptyState';
import { useDashboardStore } from '@/stores/dashboardStore';
import { useDashboardLayoutStore } from '@/stores/dashboardLayoutStore';
import type { DashboardWidget } from '@/stores/dashboardLayoutStore';
import { formatCompact, formatNumber } from '@/lib/format';
import { api } from '@/lib/api';
import { useCountUp } from '@/hooks/useCountUp';

const PIE_COLORS = ['#10b981', '#3b82f6', '#f59e0b', '#ef4444', '#8b5cf6', '#ec4899', '#06b6d4'];

const REVENUE_COLORS: Record<string, string> = {
  draft: '#64748b',
  sent: '#3b82f6',
  paid: '#10b981',
  overdue: '#ef4444',
  void: '#475569',
  partial: '#f59e0b',
};

const CHART_TOOLTIP_STYLE = {
  backgroundColor: 'rgba(15, 23, 42, 0.95)',
  backdropFilter: 'blur(12px)',
  border: '1px solid rgba(148, 163, 184, 0.12)',
  borderRadius: '8px',
  fontSize: '12px',
  color: '#f1f5f9',
  boxShadow: '0 8px 32px rgba(0, 0, 0, 0.4)',
  padding: '10px 14px',
};

const moduleLabels: Record<string, string> = {
  email: 'Email Ops',
  voice: 'Voice Ops',
  support: 'Support',
  finance: 'Finance',
  partnerships: 'Partnerships',
  growth: 'Growth Engine',
  messaging: 'Messaging Hub',
};

/* ── Widget Editing Wrapper ─────────────────────────────── */

function WidgetWrapper({
  widget,
  editing,
  children,
}: {
  widget: DashboardWidget;
  editing: boolean;
  children: React.ReactNode;
}) {
  const { toggleWidget, moveWidget } = useDashboardLayoutStore();

  if (!editing) return <>{children}</>;

  return (
    <div className="relative rounded-lg border-2 border-dashed border-surface-600/60 transition-all">
      {/* Edit header bar */}
      <div className="flex items-center justify-between gap-2 px-3 py-2 bg-surface-800/80 rounded-t-lg border-b border-surface-700/60">
        <span className="text-xs font-medium text-surface-300 truncate">{widget.title}</span>
        <div className="flex items-center gap-1 shrink-0">
          <button
            onClick={() => moveWidget(widget.id, 'up')}
            className="p-1 rounded hover:bg-surface-700 text-surface-400 hover:text-surface-200 transition-colors"
            title="Move up"
          >
            <ChevronUp className="h-3.5 w-3.5" />
          </button>
          <button
            onClick={() => moveWidget(widget.id, 'down')}
            className="p-1 rounded hover:bg-surface-700 text-surface-400 hover:text-surface-200 transition-colors"
            title="Move down"
          >
            <ChevronDown className="h-3.5 w-3.5" />
          </button>
          <button
            onClick={() => toggleWidget(widget.id)}
            className="p-1 rounded hover:bg-surface-700 text-surface-400 hover:text-surface-200 transition-colors"
            title="Hide widget"
          >
            <EyeOff className="h-3.5 w-3.5" />
          </button>
        </div>
      </div>
      {/* Dimmed content */}
      <div className="opacity-60 pointer-events-none">{children}</div>
    </div>
  );
}

/* ── Editing Panel ──────────────────────────────────────── */

function EditingPanel() {
  const { widgets, toggleWidget, moveWidget, reset, toggleEditing } = useDashboardLayoutStore();
  const sorted = [...widgets].sort((a, b) => a.order - b.order);

  return (
    <div className="glass-card rounded-lg p-5 border border-nexus-500/30 relative z-20">
      <div className="flex items-center justify-between mb-4">
        <h3 className="text-sm font-semibold text-surface-200">Customize Dashboard</h3>
        <button
          onClick={toggleEditing}
          className="p-1.5 rounded-lg hover:bg-surface-700 text-surface-400 hover:text-surface-200 transition-colors"
          title="Done editing"
        >
          <X className="h-4 w-4" />
        </button>
      </div>
      <div className="space-y-1.5">
        {sorted.map((w) => (
          <div
            key={w.id}
            className="flex items-center gap-2 px-3 py-2 rounded-lg bg-surface-800/50 hover:bg-surface-800 transition-colors"
          >
            <button
              onClick={() => toggleWidget(w.id)}
              className={`p-1 rounded transition-colors ${
                w.visible
                  ? 'text-nexus-400 hover:text-nexus-300'
                  : 'text-surface-600 hover:text-surface-400'
              }`}
              title={w.visible ? 'Hide' : 'Show'}
            >
              {w.visible ? <Eye className="h-3.5 w-3.5" /> : <EyeOff className="h-3.5 w-3.5" />}
            </button>
            <span
              className={`text-xs flex-1 truncate ${
                w.visible ? 'text-surface-300' : 'text-surface-600 line-through'
              }`}
            >
              {w.title}
            </span>
            <button
              onClick={() => moveWidget(w.id, 'up')}
              className="p-1 rounded hover:bg-surface-700 text-surface-500 hover:text-surface-300 transition-colors"
              title="Move up"
            >
              <ChevronUp className="h-3 w-3" />
            </button>
            <button
              onClick={() => moveWidget(w.id, 'down')}
              className="p-1 rounded hover:bg-surface-700 text-surface-500 hover:text-surface-300 transition-colors"
              title="Move down"
            >
              <ChevronDown className="h-3 w-3" />
            </button>
          </div>
        ))}
      </div>
      <div className="flex items-center justify-between mt-4 pt-3 border-t border-surface-700/60">
        <button
          onClick={reset}
          className="flex items-center gap-1.5 text-xs text-surface-500 hover:text-surface-300 transition-colors"
        >
          <RotateCcw className="h-3 w-3" />
          Reset to default
        </button>
        <button
          onClick={toggleEditing}
          className="px-3 py-1.5 text-xs font-medium bg-nexus-600 text-white rounded-lg hover:bg-nexus-500 transition-colors"
        >
          Done
        </button>
      </div>
    </div>
  );
}

/* ── Section Render Functions ───────────────────────────── */

function StatsSection({ stats }: { stats: NonNullable<ReturnType<typeof useDashboardStore.getState>['stats']> }) {
  const statCards = [
    { label: 'Actions Used', value: formatCompact(stats.actionsUsed ?? 0), hasProgress: true },
    { label: 'Active Contacts', value: formatCompact(stats.activeContacts ?? 0) },
    { label: 'Open Tickets', value: String(stats.openTickets ?? 0) },
    { label: 'Pending Invoices', value: formatNumber(stats.pendingInvoices ?? 0) },
    { label: 'Growth Signals', value: String(stats.growthSignals ?? 0) },
  ];

  return (
    <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-5 gap-4 relative z-10">
      {statCards.map((card, i) => (
        <div key={card.label} className="glass-stat rounded-lg p-5 gradient-border" style={{ animationDelay: `${i * 0.07}s` }}>
          <p className="text-[11px] font-medium text-surface-500 tracking-wide uppercase mb-2">{card.label}</p>
          <p className="hero-number">{card.value}</p>
          {card.hasProgress && (
            <div className="mt-4">
              <ProgressBar value={stats.actionsUsed ?? 0} max={stats.actionLimit ?? 1} size="sm" showLabel />
            </div>
          )}
        </div>
      ))}
    </div>
  );
}

function TodayStripSection({
  emailsCountUp,
  callsCountUp,
  messagesCountUp,
}: {
  emailsCountUp: number;
  callsCountUp: number;
  messagesCountUp: number;
}) {
  const todayStats = [
    { label: 'Emails', value: emailsCountUp },
    { label: 'Calls', value: callsCountUp },
    { label: 'Messages', value: messagesCountUp },
  ];

  return (
    <div className="relative z-10 glass-card rounded-lg px-4 md:px-6 py-4 flex items-center justify-between">
      <p className="text-xs font-medium text-surface-500 uppercase tracking-wider hidden sm:block">Today</p>
      <div className="flex items-center gap-4 sm:gap-8 mx-auto sm:mx-0">
        {todayStats.map((stat) => (
          <div key={stat.label} className="text-center">
            <p className="text-xl font-bold text-surface-100 tracking-tight">{stat.value}</p>
            <p className="text-[10px] text-surface-500 mt-0.5">{stat.label}</p>
          </div>
        ))}
      </div>
      <div className="w-16 hidden sm:block" />
    </div>
  );
}

function ModuleHealthSection({ stats }: { stats: NonNullable<ReturnType<typeof useDashboardStore.getState>['stats']> }) {
  const navigate = useNavigate();
  return (
    <div className="glass-card rounded-lg p-6">
      <h3 className="section-title section-title-glow mb-5">Modules</h3>
      <div className="grid grid-cols-2 sm:grid-cols-4 gap-3">
        {Object.entries(stats.moduleHealth ?? {}).map(([mod, health]) => (
          <button
            key={mod}
            onClick={() => navigate(`/${mod}`)}
            className="module-pill flex items-center gap-3 px-4 py-3 rounded-lg cursor-pointer group text-left"
          >
            <StatusDot status={health} size="md" />
            <div className="min-w-0">
              <p className="text-xs font-semibold text-surface-200 truncate group-hover:text-nexus-300 transition-colors">
                {moduleLabels[mod] || mod}
              </p>
              <p className="text-[10px] text-surface-500 capitalize">{health}</p>
            </div>
          </button>
        ))}
      </div>
    </div>
  );
}

function ActionChartSection({ data }: { data: { date: string; actions: number }[] }) {
  return (
    <div className="glass-card rounded-lg p-6">
      <div className="flex items-center justify-between mb-6">
        <h3 className="section-title section-title-glow">Action Usage</h3>
        <span className="text-[10px] text-surface-500">30 days</span>
      </div>
      <div className="h-72 chart-container">
        <ResponsiveContainer width="100%" height="100%">
          <AreaChart data={data}>
            <defs>
              <linearGradient id="actionGradient" x1="0" y1="0" x2="0" y2="1">
                <stop offset="0%" stopColor="#10b981" stopOpacity={0.35} />
                <stop offset="50%" stopColor="#10b981" stopOpacity={0.1} />
                <stop offset="100%" stopColor="#10b981" stopOpacity={0} />
              </linearGradient>
              <linearGradient id="lineGradient" x1="0" y1="0" x2="1" y2="0">
                <stop offset="0%" stopColor="#059669" />
                <stop offset="50%" stopColor="#10b981" />
                <stop offset="100%" stopColor="#34d399" />
              </linearGradient>
            </defs>
            <CartesianGrid strokeDasharray="3 3" stroke="rgba(51, 65, 85, 0.4)" vertical={false} />
            <XAxis
              dataKey="date"
              tick={{ fill: '#475569', fontSize: 10 }}
              tickLine={false}
              axisLine={false}
              interval={4}
            />
            <YAxis
              tick={{ fill: '#475569', fontSize: 10 }}
              tickLine={false}
              axisLine={false}
              width={35}
            />
            <Tooltip
              contentStyle={{
                backgroundColor: 'rgba(15, 23, 42, 0.95)',
                backdropFilter: 'blur(12px)',
                border: '1px solid rgba(148, 163, 184, 0.12)',
                borderRadius: '8px',
                fontSize: '12px',
                color: '#f1f5f9',
                boxShadow: '0 8px 32px rgba(0, 0, 0, 0.4)',
                padding: '10px 14px',
              }}
              cursor={{ stroke: 'rgba(16, 185, 129, 0.3)', strokeWidth: 1 }}
            />
            <Area
              type="monotone"
              dataKey="actions"
              stroke="url(#lineGradient)"
              strokeWidth={2.5}
              fill="url(#actionGradient)"
              dot={false}
              activeDot={{
                r: 5,
                fill: '#10b981',
                stroke: 'rgba(16, 185, 129, 0.3)',
                strokeWidth: 8,
              }}
            />
          </AreaChart>
        </ResponsiveContainer>
      </div>
    </div>
  );
}

function ActionBreakdownSection({ data }: { data: { module: string; count: number }[] }) {
  return (
    <div className="glass-card rounded-lg p-6">
      <h3 className="section-title section-title-glow mb-5">Action Breakdown</h3>
      {data.length > 0 ? (
        <div className="h-64 chart-container flex items-center">
          <ResponsiveContainer width="50%" height="100%">
            <PieChart>
              <Pie
                data={data}
                dataKey="count"
                nameKey="module"
                cx="50%"
                cy="50%"
                innerRadius={50}
                outerRadius={80}
                paddingAngle={3}
                strokeWidth={0}
              >
                {data.map((_, idx) => (
                  <Cell key={idx} fill={PIE_COLORS[idx % PIE_COLORS.length]} />
                ))}
              </Pie>
              <Tooltip contentStyle={CHART_TOOLTIP_STYLE} />
            </PieChart>
          </ResponsiveContainer>
          <div className="flex-1 space-y-2 pl-2">
            {data.map((item, idx) => (
              <div key={item.module} className="flex items-center gap-2">
                <span
                  className="w-2.5 h-2.5 rounded-full shrink-0"
                  style={{ backgroundColor: PIE_COLORS[idx % PIE_COLORS.length] }}
                />
                <span className="text-xs text-surface-400 truncate">
                  {moduleLabels[item.module] || item.module}
                </span>
                <span className="text-xs font-semibold text-surface-200 ml-auto tabular-nums">
                  {item.count}
                </span>
              </div>
            ))}
          </div>
        </div>
      ) : (
        <div className="h-64 flex items-center justify-center">
          <p className="text-sm text-surface-500">No action data yet</p>
        </div>
      )}
    </div>
  );
}

function RevenueSummarySection({ data }: { data: { status: string; total: number }[] }) {
  return (
    <div className="glass-card rounded-lg p-6">
      <h3 className="section-title section-title-glow mb-5">Revenue by Status</h3>
      {data.length > 0 ? (
        <div className="h-64 chart-container">
          <ResponsiveContainer width="100%" height="100%">
            <BarChart data={data} layout="vertical" margin={{ left: 10, right: 20, top: 4, bottom: 4 }}>
              <CartesianGrid strokeDasharray="3 3" stroke="rgba(51, 65, 85, 0.4)" horizontal={false} />
              <XAxis
                type="number"
                tick={{ fill: '#475569', fontSize: 10 }}
                tickLine={false}
                axisLine={false}
                tickFormatter={(v: number) => `$${v >= 1000 ? `${(v / 1000).toFixed(0)}k` : v}`}
              />
              <YAxis
                type="category"
                dataKey="status"
                tick={{ fill: '#94a3b8', fontSize: 11, textTransform: 'capitalize' } as object}
                tickLine={false}
                axisLine={false}
                width={65}
              />
              <Tooltip
                contentStyle={CHART_TOOLTIP_STYLE}
                formatter={(value: number) => [`$${formatNumber(value)}`, 'Amount']}
              />
              <Bar dataKey="total" radius={[0, 4, 4, 0]} barSize={22}>
                {data.map((entry) => (
                  <Cell key={entry.status} fill={REVENUE_COLORS[entry.status] ?? '#64748b'} />
                ))}
              </Bar>
            </BarChart>
          </ResponsiveContainer>
        </div>
      ) : (
        <div className="h-64 flex items-center justify-center">
          <p className="text-sm text-surface-500">No invoice data yet</p>
        </div>
      )}
    </div>
  );
}

function ChannelVolumeSection({ data }: { data: { channel: string; count: number }[] }) {
  if (data.length === 0) return null;
  return (
    <div className="glass-card rounded-lg p-6">
      <div className="flex items-center justify-between mb-5">
        <h3 className="section-title section-title-glow">Channel Volume</h3>
        <span className="text-[10px] text-surface-500">30 days</span>
      </div>
      <div className="space-y-3">
        {data.map((item, idx) => {
          const maxCount = data[0]?.count ?? 1;
          const pct = Math.max(4, (item.count / maxCount) * 100);
          return (
            <div key={item.channel} className="flex items-center gap-3">
              <span className="text-xs text-surface-400 w-20 shrink-0 capitalize">{item.channel}</span>
              <div className="flex-1 h-6 rounded bg-surface-800 overflow-hidden">
                <div
                  className="h-full rounded transition-all duration-700 ease-out"
                  style={{
                    width: `${pct}%`,
                    backgroundColor: PIE_COLORS[idx % PIE_COLORS.length],
                    opacity: 0.85,
                  }}
                />
              </div>
              <span className="text-xs font-semibold text-surface-200 tabular-nums w-10 text-right">
                {item.count}
              </span>
            </div>
          );
        })}
      </div>
    </div>
  );
}

function QuickActionsSection() {
  const navigate = useNavigate();
  const quickActions = [
    { label: 'Send Email', icon: Send, href: '/email' },
    { label: 'Create Ticket', icon: Plus, href: '/support' },
    { label: 'Add Contact', icon: UserPlus, href: '/contacts' },
    { label: 'New Invoice', icon: FileText, href: '/finance' },
  ];

  return (
    <div className="glass-card rounded-lg p-6">
      <h3 className="section-title section-title-glow mb-5">Quick Actions</h3>
      <div className="grid grid-cols-2 sm:grid-cols-4 gap-3">
        {quickActions.map((action) => (
          <button
            key={action.label}
            onClick={() => navigate(action.href)}
            className="action-btn flex items-center justify-between px-4 py-3.5 rounded-lg cursor-pointer group"
          >
            <span className="text-sm font-medium text-surface-300 group-hover:text-surface-100 transition-colors">
              {action.label}
            </span>
            <ArrowRight className="h-3.5 w-3.5 text-surface-600 group-hover:text-nexus-400 transition-colors" />
          </button>
        ))}
      </div>
    </div>
  );
}

function ActivitySection({ recentActivity }: { recentActivity: ReturnType<typeof useDashboardStore.getState>['recentActivity'] }) {
  return (
    <div className="glass-card rounded-lg p-6 sticky top-6">
      <h3 className="section-title section-title-glow mb-5">Recent Activity</h3>
      {recentActivity.length > 0 ? (
        <Timeline items={recentActivity} />
      ) : (
        <div className="text-center py-12">
          <p className="text-sm text-surface-500">No recent activity</p>
          <p className="text-xs text-surface-600 mt-1">Actions will appear here as you use Nexus</p>
        </div>
      )}
    </div>
  );
}

/* ── Main Component ─────────────────────────────────────── */

export function CommandCenter() {
  const { stats, recentActivity, isLoading, error, fetchDashboard } = useDashboardStore();
  const { widgets, editing, toggleEditing } = useDashboardLayoutStore();
  const [usageChartData, setUsageChartData] = useState<{ date: string; actions: number }[]>([]);
  const [actionBreakdown, setActionBreakdown] = useState<{ module: string; count: number }[]>([]);
  const [revenueSummary, setRevenueSummary] = useState<{ status: string; total: number }[]>([]);
  const [channelVolume, setChannelVolume] = useState<{ channel: string; count: number }[]>([]);

  // Count-up animations for Today strip numbers
  const emailsCountUp = useCountUp(stats?.emailsSentToday ?? 0);
  const callsCountUp = useCountUp(stats?.callsToday ?? 0);
  const messagesCountUp = useCountUp(stats?.messagesToday ?? 0);

  useEffect(() => {
    fetchDashboard();
    api
      .get<{ success: boolean; data: { date: string; actions: number }[] }>('/api/dashboard/usage-history')
      .then((res) => {
        setUsageChartData(
          (res.data ?? []).map((d) => ({
            date: new Date(d.date).toLocaleDateString('en-US', { month: 'short', day: 'numeric' }),
            actions: d.actions,
          })),
        );
      })
      .catch(() => {});

    api
      .get<{ success: boolean; data: { module: string; count: number }[] }>('/api/analytics/action-breakdown')
      .then((res) => setActionBreakdown(res.data ?? []))
      .catch(() => {});

    api
      .get<{ success: boolean; data: Record<string, number> }>('/api/analytics/revenue-summary')
      .then((res) => {
        const entries = Object.entries(res.data ?? {}).map(([status, total]) => ({ status, total }));
        setRevenueSummary(entries);
      })
      .catch(() => {});

    api
      .get<{ success: boolean; data: { channel: string; count: number }[] }>('/api/analytics/channel-volume')
      .then((res) => setChannelVolume(res.data ?? []))
      .catch(() => {});
  }, []);

  /* Loading skeleton */
  if (stats === null && isLoading) {
    return (
      <div className="relative max-w-[1440px] space-y-6">
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-5 gap-4">
          {Array.from({ length: 5 }).map((_, i) => (
            <div key={i} className="rounded-lg border border-surface-700 bg-surface-800/60 p-5">
              <Skeleton className="h-3 w-24 mb-3" />
              <Skeleton className="h-8 w-16" />
            </div>
          ))}
        </div>
        <Skeleton className="h-16 w-full rounded-lg" />
        <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
          <div className="lg:col-span-2 space-y-6">
            <div className="rounded-lg border border-surface-700 bg-surface-800/60 p-6">
              <Skeleton className="h-4 w-24 mb-5" />
              <div className="grid grid-cols-2 sm:grid-cols-4 gap-3">
                {Array.from({ length: 7 }).map((_, i) => (
                  <Skeleton key={i} className="h-12 rounded-lg" />
                ))}
              </div>
            </div>
            <div className="rounded-lg border border-surface-700 bg-surface-800/60 p-6">
              <Skeleton className="h-4 w-32 mb-6" />
              <Skeleton className="h-72 w-full rounded-lg" />
            </div>
          </div>
          <div>
            <div className="rounded-lg border border-surface-700 bg-surface-800/60 p-6">
              <Skeleton className="h-4 w-32 mb-5" />
              <SkeletonText lines={6} />
            </div>
          </div>
        </div>
      </div>
    );
  }

  if (stats === null && !isLoading) {
    return (
      <EmptyState
        title={error ? 'Failed to load dashboard' : 'No dashboard data'}
        description={error ?? 'Could not retrieve dashboard statistics.'}
        action={
          <button
            onClick={() => fetchDashboard()}
            className="px-4 py-2 bg-nexus-600 text-white rounded-lg text-sm font-medium hover:bg-nexus-500 transition-colors"
          >
            Retry
          </button>
        }
      />
    );
  }

  /* ── Widget renderer ──────────────────────────────────── */

  const sortedWidgets = [...widgets].sort((a, b) => a.order - b.order);

  // IDs that belong to the "left column" of the 3-col grid
  const leftColumnIds = new Set(['module-health', 'action-chart', 'action-breakdown', 'revenue-summary', 'channel-volume', 'quick-actions']);
  // IDs that go above the grid (full-width)
  const fullWidthIds = new Set(['stats', 'today-strip']);
  // Activity goes in the right column
  const rightColumnId = 'activity';

  function renderWidgetContent(w: DashboardWidget): React.ReactNode {
    switch (w.id) {
      case 'stats':
        return <StatsSection stats={stats!} />;
      case 'today-strip':
        return (
          <TodayStripSection
            emailsCountUp={emailsCountUp}
            callsCountUp={callsCountUp}
            messagesCountUp={messagesCountUp}
          />
        );
      case 'module-health':
        return <ModuleHealthSection stats={stats!} />;
      case 'action-chart':
        return <ActionChartSection data={usageChartData} />;
      case 'action-breakdown':
        return <ActionBreakdownSection data={actionBreakdown} />;
      case 'revenue-summary':
        return <RevenueSummarySection data={revenueSummary} />;
      case 'channel-volume':
        return <ChannelVolumeSection data={channelVolume} />;
      case 'quick-actions':
        return <QuickActionsSection />;
      case 'activity':
        return <ActivitySection recentActivity={recentActivity} />;
      default:
        return null;
    }
  }

  function renderWidget(w: DashboardWidget) {
    if (!w.visible && !editing) return null;
    const content = renderWidgetContent(w);
    if (!content) return null;

    if (editing) {
      return (
        <WidgetWrapper key={w.id} widget={w} editing>
          {!w.visible ? (
            <div className="p-4 text-center">
              <p className="text-xs text-surface-600">Hidden</p>
            </div>
          ) : (
            content
          )}
        </WidgetWrapper>
      );
    }

    return <div key={w.id}>{content}</div>;
  }

  // Partition widgets into full-width, left-column, and right-column
  const fullWidthWidgets = sortedWidgets.filter((w) => fullWidthIds.has(w.id));
  const leftWidgets = sortedWidgets.filter((w) => leftColumnIds.has(w.id));
  const rightWidgets = sortedWidgets.filter((w) => w.id === rightColumnId);

  // For action-breakdown and revenue-summary, render them in a 2-col sub-grid if they are adjacent
  function renderLeftWidgets() {
    const result: React.ReactNode[] = [];
    let i = 0;
    while (i < leftWidgets.length) {
      const w = leftWidgets[i];
      // Check if we should pair breakdown + revenue in a 2-col grid
      const next = leftWidgets[i + 1];
      const pairIds = new Set(['action-breakdown', 'revenue-summary']);
      if (
        pairIds.has(w.id) &&
        next &&
        pairIds.has(next.id) &&
        (w.visible || editing) &&
        (next.visible || editing)
      ) {
        result.push(
          <div key={`pair-${w.id}-${next.id}`} className="grid grid-cols-1 md:grid-cols-2 gap-6">
            {renderWidget(w)}
            {renderWidget(next)}
          </div>,
        );
        i += 2;
      } else {
        const rendered = renderWidget(w);
        if (rendered) result.push(rendered);
        i += 1;
      }
    }
    return result;
  }

  return (
    <div className="relative max-w-[1440px] space-y-6">
      {/* Ambient orbs */}
      <div className="orb orb-green w-[500px] h-[500px] -top-40 -left-40 opacity-60" />
      <div className="orb orb-blue w-[400px] h-[400px] top-60 -right-32 opacity-40" />

      {/* ── Header with Customize button ────────────────────── */}
      <div className="flex items-center justify-end relative z-20">
        <button
          onClick={toggleEditing}
          className={`flex items-center gap-2 px-3 py-1.5 text-xs font-medium rounded-lg transition-colors ${
            editing
              ? 'bg-nexus-600 text-white hover:bg-nexus-500'
              : 'bg-surface-800/60 text-surface-400 hover:text-surface-200 hover:bg-surface-700 border border-surface-700'
          }`}
        >
          <Sliders className="h-3.5 w-3.5" />
          {editing ? 'Editing...' : 'Customize'}
        </button>
      </div>

      {/* ── Editing Panel ───────────────────────────────────── */}
      {editing && <EditingPanel />}

      {/* ── Full-width widgets (stats, today strip) ─────────── */}
      {fullWidthWidgets.map((w) => renderWidget(w))}

      {/* ── Main Grid ───────────────────────────────────────── */}
      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6 relative z-10">
        {/* Left column */}
        <div className="lg:col-span-2 space-y-6">
          {renderLeftWidgets()}
        </div>

        {/* Right column — Activity */}
        <div className="space-y-6">
          {rightWidgets.map((w) => renderWidget(w))}
        </div>
      </div>
    </div>
  );
}
